import {
  onBeforeUnmount, onMounted, ref, watch, nextTick,
} from '@vue/composition-api'

const BTN_HEIGHT = 100

export default function useChartLayout({
  getChartContainer, getSlider, charts, panelRatios,
}) {
  const zoomBtnMarginTop = ref(panelRatios.value.map(() => 0))
  const moveBtnMarginTop = ref(panelRatios.value.map(() => 0))
  let resizeObserver = null
  let sliderResizeObserver = null

  const setChartsHeight = () => {
    const slider = getSlider()
    const sliderEl = slider?.$el || slider
    if (!sliderEl) return

    const { height } = sliderEl.getBoundingClientRect()
    if (height === 0) {
      // Slider not ready yet, will retry
      return
    }

    const ratios = panelRatios.value

    const prefixSum = []
    let acc = 0
    ratios.forEach(r => { prefixSum.push(acc); acc += r })

    zoomBtnMarginTop.value = prefixSum.map((p, i) => (i === 0 ? 0 : `${height * p}px`))
    moveBtnMarginTop.value = ratios.map((r, i) => `${height * prefixSum[i] + (height * r - BTN_HEIGHT) / 2}px`)

    charts.forEach((chart, idx) => {
      if (chart) {
        const chartHeight = Math.max(1, Math.round(height * ratios[idx]))
        chart.applyOptions({ height: chartHeight })
      }
    })
  }

  const setChartsWidth = () => {
    const container = getChartContainer()
    if (!container) return
    const { width } = container.getBoundingClientRect()
    charts.forEach(chart => {
      if (chart) chart.applyOptions({ width })
    })
  }

  const onResize = () => {
    setChartsHeight()
    setChartsWidth()
  }

  onMounted(async () => {
    // Wait for Vue to finish rendering
    await nextTick()

    // Try to set height immediately
    setChartsHeight()
    setChartsWidth()

    // Also observe the slider itself for size changes
    const slider = getSlider()
    const sliderEl = slider?.$el || slider
    if (sliderEl && !sliderResizeObserver) {
      sliderResizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(onResize)
      })
      sliderResizeObserver.observe(sliderEl)
    }

    const container = getChartContainer()
    if (container) {
      resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(onResize)
      })
      resizeObserver.observe(container)
    }

    window.addEventListener('resize', onResize)

    // Retry after a short delay in case initial measurement failed
    setTimeout(() => {
      setChartsHeight()
      setChartsWidth()
    }, 200)
  })

  onBeforeUnmount(() => {
    if (resizeObserver) resizeObserver.disconnect()
    if (sliderResizeObserver) sliderResizeObserver.disconnect()
    window.removeEventListener('resize', onResize)
  })

  watch(panelRatios, () => setChartsHeight())

  return {
    zoomBtnMarginTop, moveBtnMarginTop, setChartsHeight, setChartsWidth,
  }
}
